/**
 * Moeda por país e moeda do RESTAURANTE — UMA fonte para toda a plataforma
 * (#299 decisão B, spec-arq-6392664563; #171 critério 11).
 *
 * Usado pelo Menu público, pelo config do painel, pelo import (defaults,
 * vista, commit) e pelo cadastro self-serve: a moeda do país é o default do
 * wizard e do backfill da migração 0136 — o mapa cobre TODOS os países ISO
 * 3166-1 (o seed de countries liga enabled_signup para todos; um país sem
 * moeda aqui seria um 500 COUNTRY_CURRENCY_MISSING no cadastro).
 *
 * Dados estáticos ISO 3166-1 → ISO 4217 ativa (inclui territórios: XCG nos
 * ex-ANTILHAS desde 2025, ZWG no Zimbabué, USD nos territórios dos EUA…).
 */
import { SUPPORTED_CURRENCIES, type SupportedCurrency } from '../currency.js'

/** Zona do euro (ISO 3166-1 alpha-2), microestados e territórios que usam o euro. */
const EURO_COUNTRIES = new Set<string>([
  'AD',
  'AT',
  'AX',
  'BE',
  'BL',
  'CY',
  'DE',
  'EE',
  'ES',
  'FI',
  'FR',
  'GF',
  'GP',
  'GR',
  'HR',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MC',
  'ME',
  'MF',
  'MQ',
  'MT',
  'NL',
  'PM',
  'PT',
  'RE',
  'SI',
  'SK',
  'SM',
  'TF',
  'VA',
  'XK',
  'YT',
])

/**
 * País (ISO 3166-1 alpha-2) → moeda ISO 4217 suportada pela plataforma.
 * Agrupado POR MOEDA (uma linha = uma moeda, países separados por espaço) —
 * é a MESMA partição do backfill da migração 0136 (o teste unitário compara
 * os dois).
 */
const CURRENCY_GROUPS: Record<string, string> = {
  AED: 'AE',
  AFN: 'AF',
  ALL: 'AL',
  AMD: 'AM',
  AOA: 'AO',
  ARS: 'AR',
  AWG: 'AW',
  AZN: 'AZ',
  BAM: 'BA',
  BBD: 'BB',
  BDT: 'BD',
  BGN: 'BG',
  BHD: 'BH',
  BIF: 'BI',
  BMD: 'BM',
  BND: 'BN',
  BOB: 'BO',
  BRL: 'BR',
  BSD: 'BS',
  BTN: 'BT',
  BWP: 'BW',
  BYN: 'BY',
  BZD: 'BZ',
  CAD: 'CA',
  CDF: 'CD',
  CHF: 'CH LI',
  CLP: 'CL',
  CNY: 'CN',
  COP: 'CO',
  CRC: 'CR',
  CUP: 'CU',
  CVE: 'CV',
  DJF: 'DJ',
  DKK: 'DK FO GL',
  DOP: 'DO',
  DZD: 'DZ',
  EGP: 'EG',
  ERN: 'ER',
  ETB: 'ET',
  FJD: 'FJ',
  FKP: 'FK',
  GEL: 'GE',
  GHS: 'GH',
  GIP: 'GI',
  GMD: 'GM',
  GNF: 'GN GW',
  GTQ: 'GT',
  GYD: 'GY',
  HKD: 'HK',
  HNL: 'HN',
  HTG: 'HT',
  HUF: 'HU',
  IDR: 'ID',
  ILS: 'IL PS',
  INR: 'IN',
  IQD: 'IQ',
  IRR: 'IR',
  ISK: 'IS',
  JMD: 'JM',
  JOD: 'JO',
  JPY: 'JP',
  KES: 'KE',
  KGS: 'KG',
  KHR: 'KH',
  KMF: 'KM',
  KPW: 'KP',
  KRW: 'KR',
  KWD: 'KW',
  KYD: 'KY',
  KZT: 'KZ',
  LAK: 'LA',
  LBP: 'LB',
  LKR: 'LK',
  LRD: 'LR',
  LSL: 'LS',
  LYD: 'LY',
  MAD: 'MA EH',
  MDL: 'MD',
  MGA: 'MG',
  MKD: 'MK',
  MMK: 'MM',
  MNT: 'MN',
  MOP: 'MO',
  MRU: 'MR',
  MUR: 'MU',
  MVR: 'MV',
  MWK: 'MW',
  MXN: 'MX',
  MYR: 'MY',
  MZN: 'MZ',
  NAD: 'NA',
  NGN: 'NG',
  NIO: 'NI',
  NPR: 'NP',
  OMR: 'OM',
  PAB: 'PA',
  PEN: 'PE',
  PGK: 'PG',
  PHP: 'PH',
  PKR: 'PK',
  PLN: 'PL',
  PYG: 'PY',
  QAR: 'QA',
  RON: 'RO',
  RSD: 'RS',
  RUB: 'RU',
  RWF: 'RW',
  SAR: 'SA',
  SBD: 'SB',
  SCR: 'SC',
  SDG: 'SD',
  SEK: 'SE',
  SGD: 'SG',
  SHP: 'SH',
  SLE: 'SL',
  SOS: 'SO',
  SRD: 'SR',
  SSP: 'SS',
  STN: 'ST',
  SYP: 'SY',
  SZL: 'SZ',
  THB: 'TH',
  TJS: 'TJ',
  TMT: 'TM',
  TND: 'TN',
  TOP: 'TO',
  TRY: 'TR',
  TTD: 'TT',
  TWD: 'TW',
  TZS: 'TZ',
  UAH: 'UA',
  UGX: 'UG',
  UYU: 'UY',
  UZS: 'UZ',
  VES: 'VE',
  VND: 'VN',
  VUV: 'VU',
  WST: 'WS',
  XAF: 'CF CG CM GA GQ TD',
  XCD: 'AG AI DM GD KN LC MS VC',
  XCG: 'CW SX',
  XPF: 'NC PF WF',
  YER: 'YE',
  ZAR: 'ZA',
  ZMW: 'ZM',
  ZWG: 'ZW',
  USD: 'AS AQ BQ EC FM GU IO MH MP PR PW SV TC TL UM US VG VI',
  AUD: 'AU CC CX HM KI NF NR TV',
  NZD: 'NU NZ CK TK PN',
  CZK: 'CZ',
  GBP: 'GB GS GG IM JE',
  NOK: 'NO BV SJ',
  XOF: 'BF BJ CI ML NE SN TG',
}

const CURRENCY_BY_COUNTRY: Record<string, SupportedCurrency> = Object.fromEntries(
  Object.entries(CURRENCY_GROUPS).flatMap(([currency, countries]) =>
    countries.split(' ').map((cc) => [cc, currency as SupportedCurrency]),
  ),
)

/**
 * #486 item 1: TODOS os códigos do mapa (249 países ISO 3166-1 + XK, código
 * de uso exclusivo para o Kosovo) — fonte do seletor de país do painel. O
 * dono escolhe qualquer país do mundo, não só os de signup habilitado ou os
 * que têm tradução.
 */
export const ALL_COUNTRY_CODES: readonly string[] = [
  ...new Set([...EURO_COUNTRIES, ...Object.keys(CURRENCY_BY_COUNTRY)]),
].sort()

/** País presente no mapa país → moeda (seletor do painel e PUT /admin/config). */
export function isSupportedCountry(code: string | null | undefined): boolean {
  const cc = code?.trim().toUpperCase()
  return !!cc && (ALL_COUNTRY_CODES as readonly string[]).includes(cc)
}

/** Moeda do país (zona do euro = EUR); desconhecido → null. */
export function currencyForCountry(country: string | null | undefined): SupportedCurrency | null {
  const cc = country?.trim().toUpperCase()
  if (!cc) return null
  if (EURO_COUNTRIES.has(cc)) return 'EUR'
  return CURRENCY_BY_COUNTRY[cc] ?? null
}

/**
 * Moeda do RESTAURANTE — a MESMA para o import, o Menu, o carrinho e o
 * painel: `restaurant_config.currency` (coluna criada pela #171, migração
 * 0136), senão o país do tenant (`tenants.country_code`). Sem nenhuma →
 * null (o import recusa com 422 RESTAURANT_CURRENCY_MISSING; Menu/config
 * apresentam EUR por omissão).
 */
export function resolveRestaurantCurrency(input: {
  configCurrency?: string | null
  countryCode?: string | null
}): SupportedCurrency | null {
  const config = input.configCurrency?.trim().toUpperCase()
  if (config && (SUPPORTED_CURRENCIES as readonly string[]).includes(config)) {
    return config as SupportedCurrency
  }
  return currencyForCountry(input.countryCode)
}

/**
 * #171: a moeda é aceita no cadastro e no PUT /admin/config? ISO 4217
 * presente na lista de moedas suportadas (que inclui todos os valores do
 * mapa país → moeda). Entrada já em maiúsculas e sem espaços.
 */
export function isSupportedCurrency(code: string | null | undefined): boolean {
  const c = code?.trim().toUpperCase()
  return !!c && (SUPPORTED_CURRENCIES as readonly string[]).includes(c)
}

/**
 * #171/#419: idioma principal do Menu por omissão do país — SEM região
 * ('pt', 'es', 'fr'…). O dono edita no wizard; o que se propõe aqui é só o
 * ponto de partida:
 * - lusofonia → pt; países de língua oficial única → o código dela;
 * - BE → fr, CH → de (proposta técnica à espera do dono, issue #171);
 * - qualquer outro → 'pt' (língua fundadora da plataforma, comportamento
 *   anterior ao mapa — sempre editável).
 */
const LOCALE_BY_COUNTRY: Record<string, string> = {
  PT: 'pt',
  BR: 'pt',
  AO: 'pt',
  MZ: 'pt',
  CV: 'pt',
  ES: 'es',
  MX: 'es',
  AR: 'es',
  CL: 'es',
  CO: 'es',
  PE: 'es',
  UY: 'es',
  FR: 'fr',
  BE: 'fr',
  DE: 'de',
  AT: 'de',
  CH: 'de',
  LI: 'de',
  IT: 'it',
  NL: 'nl',
  GB: 'en',
  US: 'en',
  IE: 'en',
  CA: 'en',
  AU: 'en',
  NZ: 'en',
  ZA: 'en',
  SG: 'en',
  PH: 'en',
  DK: 'da',
  SE: 'sv',
  NO: 'no',
  IS: 'is',
  FI: 'fi',
  PL: 'pl',
  CZ: 'cs',
  HU: 'hu',
  RO: 'ro',
  BG: 'bg',
  GR: 'el',
  TR: 'tr',
  RU: 'ru',
  UA: 'uk',
  MA: 'fr',
  EG: 'ar',
  SA: 'ar',
  AE: 'ar',
  QA: 'ar',
  KW: 'ar',
  BH: 'ar',
  OM: 'ar',
  IL: 'he',
  JP: 'ja',
  KR: 'ko',
  CN: 'zh',
  TW: 'zh',
  HK: 'zh',
  IN: 'hi',
  TH: 'th',
  ID: 'id',
  VN: 'vi',
}

/** Idioma sem região proposto para o país; desconhecido → 'pt'. */
export function defaultLocaleForCountry(country: string | null | undefined): string {
  const cc = country?.trim().toUpperCase()
  return (cc && LOCALE_BY_COUNTRY[cc]) || 'pt'
}
